import {canViewFootballAnalytics} from "../components/football-intelligence/analyticsAccess";
// File: src/pages/ClubPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import TeamProfileView from "../components/football-app/TeamProfileView";
import { useIntelligence } from "../components/football-app/playerData";
import { api } from "../api";
import SportsNewsSection from "../components/SportsNewsSection";
import HighlightVideosSection from "../components/HighlightVideosSection";
import MediaWatchSection from "../components/MediaWatchSection";
import ClubRoomSection from "../components/ClubRoomSection";

function safeNum(x, d = 0) {
  if (x === null || x === undefined || x === "") return d;
  const n = Number(x);
  return Number.isFinite(n) ? n : d;
}

function nameFromSlug(value, teamId) {
  const name = String(value || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
  return name || `Team ${teamId}`;
}

/**
 * ✅ Robust extractor for /football/club/stats payloads
 * Supports common wrapper shapes:
 * - { stats: { response: {...} } }
 * - { stats: { response: [ {...} ] } }
 * - { response: {...} }
 * - { response: [ {...} ] }
 */
function pickLeagueSummary(statsPayload) {
  const raw = statsPayload?.stats ?? statsPayload ?? null;

  // resolve "response" regardless of wrapper
  let r =
    raw?.response ??
    raw?.data?.response ??
    raw?.stats?.response ??
    raw?.stats?.data?.response ??
    raw;

  // sometimes response is an array (API-Football style)
  if (Array.isArray(r)) r = r[0];

  if (!r || typeof r !== "object") return null;

  if (r.fixtures?.played?.total == null) return null;
  const fixtures = r.fixtures || {};
  const goals = r.goals || {};

  const played = safeNum(fixtures?.played?.total, 0);
  const wins = safeNum(fixtures?.wins?.total, 0);
  const draws = safeNum(fixtures?.draws?.total, 0);
  const losses = safeNum(fixtures?.loses?.total, 0);

  const gf = safeNum(goals?.for?.total?.total, 0);
  const ga = safeNum(goals?.against?.total?.total, 0);

  // API-Football averages often come through as strings, safeNum handles it
  const avgGf = safeNum(goals?.for?.average?.total, 0);
  const avgGa = safeNum(goals?.against?.average?.total, 0);

  const form = typeof r.form === "string" ? r.form : null;

  return { played, wins, draws, losses, gf, ga, avgGf, avgGa, form, source: statsPayload?.source, warning: statsPayload?.warning };
}

function inferLeagueIdFromFixtures(fixturesPayload) {
  const played = Array.isArray(fixturesPayload?.played) ? fixturesPayload.played : [];
  const withLeague = played.find((m) => Number.isFinite(Number(m.league_id)));
  return withLeague ? Number(withLeague.league_id) : null;
}

const LOOKBACK_OPTIONS = [5, 10, 15];
const CLUB_TABS = ["overview", "matches", "squad", "statistics", "tactics", "news", "highlights", "community"];
const LEGACY_TAB = { fixtures: "matches", community: "community", highlights: "highlights" };
const CURRENT_SEASON = new Date().getFullYear();
const SEASON_OPTIONS = [CURRENT_SEASON - 2, CURRENT_SEASON - 1, CURRENT_SEASON];

function fixtureDateValue(fixture) {
  const value = new Date(fixture?.date || fixture?.kickoff || 0).getTime();
  return Number.isFinite(value) ? value : 0;
}

function fixtureOutcome(fixture, teamId, teamName) {
  const rawHomeGoals = fixture?.home_goals;
  const rawAwayGoals = fixture?.away_goals;
  if (rawHomeGoals === null || rawHomeGoals === undefined || rawAwayGoals === null || rawAwayGoals === undefined) return null;
  const homeGoals = safeNum(rawHomeGoals, null);
  const awayGoals = safeNum(rawAwayGoals, null);
  if (homeGoals === null || awayGoals === null) return null;
  if (homeGoals === awayGoals) return "D";

  const homeId = safeNum(
    fixture?.home_team_id ?? fixture?.home_id ?? fixture?.teams?.home?.id,
    null
  );
  const normalizedTeam = String(teamName || "").trim().toLowerCase();
  const normalizedHome = String(fixture?.home || fixture?.home_name || "").trim().toLowerCase();
  const isHome = homeId !== null
    ? homeId === Number(teamId)
    : normalizedHome === normalizedTeam;
  const teamWon = isHome ? homeGoals > awayGoals : awayGoals > homeGoals;
  return teamWon ? "W" : "L";
}

export default function ClubPage() {
  // ✅ route should match App.jsx: "/clubs/:teamId/:slug"
  const { teamId, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();

  const team_id = Number(teamId);

  // Query params (shareable links)
  const season = safeNum(sp.get("season"), CURRENT_SEASON);
  const league_id = sp.get("league") ? safeNum(sp.get("league"), null) : null;

  const lookback = safeNum(sp.get("lookback"), 5);
  const scope = (sp.get("scope") || "all").toLowerCase(); // "all" | "league"

  const effectiveLookback = LOOKBACK_OPTIONS.includes(lookback) ? lookback : 5;
  const leagueOnly = scope === "league" && Number.isFinite(league_id);

  const [overview, setOverview] = useState(null);
  const [fixtures, setFixtures] = useState(null);
  const [leagueStats, setLeagueStats] = useState(null);
  const [squad, setSquad] = useState([]);
  const [err, setErr] = useState(null);
  const auth = useAuth();
  const isAdmin = !!auth?.isAdmin;
  const analyticsAllowed = canViewFootballAnalytics(isAdmin);
  const requestedTab = String(sp.get("tab") || "").toLowerCase();
  const activeTab = CLUB_TABS.includes(requestedTab) ? requestedTab : LEGACY_TAB[requestedTab] || "overview";
  const [intelFilters, setIntelFilters] = useState({ last_n: 10 });
  const intel = useIntelligence({ kind: "team", identity: teamId, enabled: analyticsAllowed && !!teamId, filters: intelFilters });

  const displayName = useMemo(() => {
    return overview?.team?.name || location.state?.club?.name || intel.data?.profile?.name || nameFromSlug(slug, team_id) || `Team ${team_id}`;
  }, [overview, location.state, slug, team_id, intel.data]);
  const sport = "football";

  const venue = overview?.team?.venue || {};
  const logo = overview?.team?.logo || location.state?.club?.crest || intel.data?.profile?.image_url || null;

  const recentForm = Array.isArray(overview?.recent_form) ? overview.recent_form : [];

  const seasonLeagueSummary = useMemo(() => {
    if (!leagueStats) return null;
    return pickLeagueSummary(leagueStats);
  }, [leagueStats]);

  // ---------- query helper ----------
  function setQuery(next) {
    const cur = Object.fromEntries([...sp.entries()]);
    const merged = { ...cur, ...next };

    // clean empties
    Object.keys(merged).forEach((k) => {
      if (merged[k] === undefined || merged[k] === null || merged[k] === "") delete merged[k];
    });

    setSp(merged, { replace: true });
  }

  function onToggleScope() {
    if (!league_id) return; // can’t go league-only without league_id
    setQuery({ scope: leagueOnly ? "all" : "league" });
  }

  // ---------- LOAD ----------
  useEffect(() => {
    const load = async () => {
      try {
        setErr(null);
        setOverview(null);
        setFixtures(null);
        setLeagueStats(null);
        setSquad([]);

        const fallbackOverview = {
          team: {
            id: team_id,
            name: location.state?.club?.name || nameFromSlug(slug, team_id),
            logo: location.state?.club?.crest || null,
            venue: {},
          },
          recent_form: [],
          form_summary: {},
        };
        const emptyFixtures = { played: [], upcoming: [], next: [] };
        const calls = [
          api.get("/football/club/overview", {
            params: {
              team_id,
              season,
              league_id: league_id ?? undefined,
              lookback: effectiveLookback,
            },
          }).catch((error) => {
            console.warn("Club overview unavailable; using route fallback.", error);
            return { data: fallbackOverview };
          }),
          api.get("/football/club/fixtures", {
            params: {
              team_id,
              season,
              last: 12,
              next: 0,
              league_only: leagueOnly,
              league_id: leagueOnly ? league_id : undefined,
            },
          }).catch((error) => {
            console.warn("Club fixtures unavailable; keeping the club page accessible.", error);
            return { data: emptyFixtures };
          }),
          api.get("/football/club/squad", { params: { team_id, season } }).catch(() => ({ data: null })),
        ];

        // ✅ Full-season league stats (only when league_id is known)
        if (league_id) {
          calls.push(
            api.get("/football/club/stats", {
              params: { team_id, league_id, season },
            }).catch((error) => {
              console.warn("Club league stats unavailable.", error);
              return { data: null };
            })
          );
        }

        const res = await Promise.all(calls);

        const ov = res[0]?.data;
        const fx = res[1]?.data;
        const squadPayload = res[2]?.data;
        const st = league_id ? res[3]?.data : null;

        setOverview(ov || fallbackOverview);
        setFixtures(fx || emptyFixtures);
        setLeagueStats(st);
        setSquad(squadPayload?.players || squadPayload?.squad || squadPayload?.response || []);
      } catch (e) {
        console.error(e);
        setOverview({
          team: { id: team_id, name: location.state?.club?.name || nameFromSlug(slug, team_id), logo: location.state?.club?.crest || null, venue: {} },
          recent_form: [],
          form_summary: {},
        });
        setFixtures({ played: [], upcoming: [], next: [] });
        setErr("Some club data is temporarily unavailable. Community features are still open.");
      }
    };

    if (team_id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team_id, season, league_id, effectiveLookback, leagueOnly, location.state, slug]);

  // ✅ V2: auto-infer league_id from fixtures + update URL (shareable)
  useEffect(() => {
    if (league_id) return; // already have it
    if (!fixtures) return;

    const inferred = inferLeagueIdFromFixtures(fixtures);
    if (!inferred) return;

    setQuery({ league: String(inferred) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtures, league_id]);

  if (!overview || !fixtures) return <div className="fa"><div className="fa-wrap" style={{ paddingTop: 24 }}><div className="fa-skeleton" role="status" aria-label="Loading club" style={{ height: 130 }} /></div></div>;

  const played = Array.isArray(fixtures.played) ? fixtures.played : [];
  const upcoming = Array.isArray(fixtures.upcoming) ? fixtures.upcoming : Array.isArray(fixtures.next) ? fixtures.next : [];
  const allFixtures = [...played, ...upcoming]
    .filter((fixture, index, items) => items.findIndex((item) => (
      item.fixture_id
        ? item.fixture_id === fixture.fixture_id
        : `${item.home}-${item.away}-${item.date}` === `${fixture.home}-${fixture.away}-${fixture.date}`
    )) === index)
    .sort((a, b) => fixtureDateValue(a) - fixtureDateValue(b));

  const fixtureRows = allFixtures.map((fx) => ({ ...fx, outcome: fixtureOutcome(fx, team_id, displayName) }));
  // Player profiles need a match context; use the club's most recent played match.
  const contextFixture = [...fixtureRows].filter((f) => f.outcome).sort((a, b) => fixtureDateValue(b) - fixtureDateValue(a))[0];
  const playerHref = (pid) => (contextFixture ? `/player/${pid}?fixture_id=${contextFixture.fixture_id}` : null);

  const controls = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <div className="fa-filters" style={{ margin: 0, width: "100%" }}>
        <label>Season
          <select value={season} onChange={(e) => setQuery({ season: e.target.value })}>
            {SEASON_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label>Form window
          <select value={effectiveLookback} onChange={(e) => setQuery({ lookback: e.target.value })}>
            {LOOKBACK_OPTIONS.map((n) => <option key={n} value={n}>Last {n}</option>)}
          </select>
        </label>
        <label>Fixtures
          <select value={leagueOnly ? "league" : "all"} onChange={onToggleScope} disabled={!league_id}>
            <option value="all">All competitions</option>
            <option value="league">League only</option>
          </select>
        </label>
      </div>
    </div>
  );

  return (
    <>
      {err ? <p className="fa-empty" role="alert" style={{ margin: 0 }}>{err}</p> : null}
      <TeamProfileView
        team={{ name: displayName, logo, venue }}
        fixtures={fixtureRows}
        recentForm={recentForm.slice(0, effectiveLookback)}
        formWindow={effectiveLookback}
        seasonSummary={seasonLeagueSummary}
        squad={squad}
        season={season}
        tab={activeTab}
        onTab={(tab) => setQuery({ tab })}
        isAdmin={isAdmin}
      analyticsAllowed={analyticsAllowed}
        intel={intel}
        filters={intelFilters}
        onFilter={(key, value) => setIntelFilters((f) => ({ ...f, [key]: value || undefined }))}
        onNavigate={navigate}
        controls={controls}
        playerHref={(pid) => playerHref(pid)}
        renderNews={() => <SportsNewsSection mode="team" teamName={displayName} title="Club News" limit={8} dark />}
        renderHighlights={() => (
          <HighlightVideosSection mode="team" teamName={displayName} title={`${displayName} Highlights`} limit={18} sinceDays={90} dark featured={false} hideWhenEmpty={false} emptyText="No highlights matched to this club yet." />
        )}
        renderCommunity={() => (
          <>
            <ClubRoomSection teamName={displayName} teamLogo={logo} sport={sport} leagueKey={String(league_id || "")} leagueName={location.state?.club?.league || ""} dark />
            <MediaWatchSection mode="team" teamName={displayName} title="Media Watch" subtitle="From around the game, kept separate from fan posts." limit={8} sinceDays={14} dark emptyText="No major media posts matched to this club yet." hideWhenEmpty={false} />
          </>
        )}
      />
    </>
  );
}
